import { InputState } from '@/utils/inputState';
import { ExpandMore } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    Collapse,
    FormControlLabel,
    Popover,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';

import { AxleChip } from '../displayFields/chips';
import { VerticalStack } from '../utils/linearLayouts';
import { FieldWithAxleLabel } from './AxleLabel';

export type TagGroup = {
    id: string;
    name: string;
    tags: { id: string; name: string }[];
};

export const TagGroupSection = ({
    group,
    filteredTags,
    allSelected,
    someSelected,
    selectedIds,
    onToggleGroup,
    onToggleTag,
}: {
    group: TagGroup;
    filteredTags: { id: string; name: string }[];
    allSelected: boolean;
    someSelected: boolean;
    selectedIds: Set<string>;
    onToggleGroup: () => void;
    onToggleTag: (tag: { id: string; name: string }) => void;
}) => {
    const [expanded, setExpanded] = useState(true);

    return (
        <Box sx={{ mb: 0.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1,
                    px: 0.5,
                }}
                onClick={() => setExpanded((prev) => !prev)}
            >
                <Checkbox
                    size='small'
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleGroup();
                    }}
                    sx={{ p: 0.5 }}
                />
                <Typography variant='subtitle2' sx={{ ml: 0.5, flex: 1, userSelect: 'none' }}>
                    {group.name}
                </Typography>
                <ExpandMore
                    sx={{
                        color: 'text.secondary',
                        transition: 'transform 0.2s',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                />
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ pl: 3 }}>
                    {filteredTags.map((tag) => (
                        <FormControlLabel
                            key={tag.id}
                            sx={{ display: 'flex', m: 0 }}
                            control={
                                <Checkbox
                                    size='small'
                                    checked={selectedIds.has(tag.id)}
                                    onChange={() => onToggleTag(tag)}
                                    sx={{ p: 0.5 }}
                                />
                            }
                            label={<Typography variant='body2'>{tag.name}</Typography>}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export const TagGroupSearchableList = ({
    tagGroups,
    selectedIds,
    onToggleGroup,
    onToggleTag,
    search,
    onSearchChange,
}: {
    tagGroups: TagGroup[];
    selectedIds: Set<string>;
    onToggleGroup: (filteredTags: { id: string; name: string }[], group: TagGroup) => void;
    onToggleTag: (tag: { id: string; name: string }, group: TagGroup) => void;
    search: string;
    onSearchChange: (value: string) => void;
}) => {
    const searchLower = search.toLowerCase();

    return (
        <>
            <TextField
                size='small'
                placeholder='Search tags...'
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                fullWidth
                autoFocus
                sx={{ mb: 1.5 }}
            />
            {tagGroups.map((group) => {
                const filteredTags = search
                    ? group.tags.filter((t) => t.name.toLowerCase().includes(searchLower))
                    : group.tags;

                if (search && filteredTags.length === 0) return null;

                const allSelected =
                    filteredTags.length > 0 && filteredTags.every((t) => selectedIds.has(t.id));
                const someSelected = filteredTags.some((t) => selectedIds.has(t.id));

                return (
                    <TagGroupSection
                        key={group.id}
                        group={group}
                        filteredTags={filteredTags}
                        allSelected={allSelected}
                        someSelected={someSelected}
                        selectedIds={selectedIds}
                        onToggleGroup={() => onToggleGroup(filteredTags, group)}
                        onToggleTag={(tag) => onToggleTag(tag, group)}
                    />
                );
            })}
            {tagGroups.length === 0 && (
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ py: 2, textAlign: 'center' }}
                >
                    No tag groups found
                </Typography>
            )}
        </>
    );
};

export const TagGroupCheckboxSelect = ({
    label,
    tagGroups,
    inputState,
}: {
    label: string;
    tagGroups: TagGroup[];
    inputState: InputState<string[]>;
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const anchorRef = useRef<HTMLDivElement>(null);
    const selectedIds = useMemo(() => new Set<string>(inputState.value), [inputState.value]);

    const selectedTags = useMemo(
        () => tagGroups.flatMap((g) => g.tags).filter((t) => selectedIds.has(t.id)),
        [tagGroups, selectedIds],
    );

    const toggleTag = useCallback(
        (tagId: string) => {
            if (selectedIds.has(tagId)) {
                inputState.setInputState(inputState.value.filter((id) => id !== tagId));
            } else {
                inputState.setInputState([...inputState.value, tagId]);
            }
        },
        [selectedIds, inputState],
    );

    const toggleGroup = useCallback(
        (tags: { id: string; name: string }[]) => {
            const allSelected = tags.every((t) => selectedIds.has(t.id));
            if (allSelected) {
                const groupIds = new Set(tags.map((t) => t.id));
                inputState.setInputState(inputState.value.filter((id) => !groupIds.has(id)));
            } else {
                const newIds = tags.filter((t) => !selectedIds.has(t.id)).map((t) => t.id);
                inputState.setInputState([...inputState.value, ...newIds]);
            }
        },
        [selectedIds, inputState],
    );

    return (
        <FieldWithAxleLabel label={label}>
            <Box
                ref={anchorRef}
                onClick={() => setOpen(true)}
                sx={(theme) => ({
                    border: 1,
                    borderColor:
                        theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.23)'
                            : 'rgba(255, 255, 255, 0.23)',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    '&:hover': { borderColor: 'text.primary' },
                })}
            >
                <Typography
                    variant='body1'
                    color={selectedTags.length ? 'text.primary' : 'text.secondary'}
                >
                    {selectedTags.length ? `${selectedTags.length} selected` : 'Select...'}
                </Typography>
                <ExpandMore
                    sx={{
                        color: 'action.active',
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                />
            </Box>
            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => {
                    setOpen(false);
                    setSearch('');
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            width: anchorRef.current?.offsetWidth ?? 320,
                            mt: 0.5,
                        },
                    },
                }}
            >
                <Box sx={{ maxHeight: 400, overflow: 'auto', p: 2, scrollbarGutter: 'stable' }}>
                    <TagGroupSearchableList
                        tagGroups={tagGroups}
                        selectedIds={selectedIds}
                        search={search}
                        onSearchChange={setSearch}
                        onToggleGroup={(filteredTags) => toggleGroup(filteredTags)}
                        onToggleTag={(tag) => toggleTag(tag.id)}
                    />
                </Box>
            </Popover>
            {selectedTags.length > 0 && (
                <Box mt={1}>
                    <VerticalStack spacing={1}>
                        {selectedTags.map((tag) => (
                            <AxleChip
                                key={tag.id}
                                label={tag.name}
                                tooltip={tag.name}
                                onAction={() => toggleTag(tag.id)}
                                fullWidth
                            />
                        ))}
                    </VerticalStack>
                </Box>
            )}
        </FieldWithAxleLabel>
    );
};
